/**
 * Library feature module (W2-10 projects-data; DESIGN.md §3.1, §4.1, §5.13, §5.16; WORKPACKAGES Appendix B:
 * `project.*` except `project.new`, `project.settings`; the History module registers `project.checkpoint` and
 * `project.versions`).
 *
 * Provides:
 *  - routes `#/` (Library) and `#/trash` (Trash);
 *  - commands on a chart: `project.open`, `project.newDifficulty`, `project.duplicate`, `project.export`,
 *    `project.rename`, `project.trash`, `project.restore`, `project.deleteForever`, `project.recoveryData`; and on
 *    the Library: `project.emptyTrash`, `project.showTrash`, `project.backupAll`, `project.protect`.
 *    A chart command takes `{ id }` (toast actions, other modules) or acts on the focused card of the Library, else
 *    on the chart open in the editor;
 *  - help topics about the Library and keeping charts safe;
 *  - `init`: answers "Switch to it" requests of other tabs for the chart open here (focus, blinking title, toast).
 */

import type { AppServices, CommandContext, CommandDef, FeatureModule, HelpTopicDef } from '../../app/registry/types';
import type { ProjectId } from '../../core/model/types';
import type { ProjectSummary } from '../../persist/types';
import { m } from './library.messages';
import {
  backupAll,
  deleteForever,
  displayName,
  downloadRecoveryData,
  duplicateProject,
  exportProject,
  findProject,
  openProject,
  protectCharts,
  refreshProjects,
  renameProject,
  restoreProject,
  trashProject,
} from './libraryActions';
import { libraryStore, trashedProjects } from './libraryStore';
import { LibraryRoute, TrashRoute } from './LibraryRoute';
import { blinkTitle, listenFocusRequests } from './tabBus';

// ---------------------------------------------------------------------------------------------------------------
// Targets
// ---------------------------------------------------------------------------------------------------------------

function idArg(args: unknown): ProjectId | null {
  if (typeof args !== 'object' || args === null || Array.isArray(args)) return null;
  const id = (args as Readonly<Record<string, unknown>>).id;
  return typeof id === 'string' && id !== '' ? (id as ProjectId) : null;
}

/** The chart a command acts on: `{ id }`, else the Library's focused card, else the chart open in the editor. */
export async function commandTarget(ctx: AppServices, args: unknown): Promise<ProjectSummary | null> {
  const lib = libraryStore(ctx);
  const explicit = idArg(args);
  const id =
    explicit ?? (lib.get().mounted > 0 ? lib.get().focused : null) ?? ctx.store?.projectId ?? lib.get().focused;
  if (id === null) return null;
  return findProject(ctx, id);
}

function noTarget(ctx: CommandContext): void {
  ctx.ui.toast({ key: m.noTarget }, { kind: 'info' });
}

/** A command over one chart (`{ id }`, focused card or open chart). */
function chartCommand(
  def: Omit<CommandDef, 'run'>,
  run: (ctx: CommandContext, p: ProjectSummary, args: unknown) => Promise<unknown>,
): CommandDef {
  return {
    ...def,
    async run(ctx, args) {
      const p = await commandTarget(ctx, args);
      if (p === null) {
        noTarget(ctx);
        return;
      }
      await run(ctx, p, args);
    },
  };
}

/** Text argument of a palette prompt (`project.rename` with the new title). */
function textArg(args: unknown, key: string): string | null {
  if (typeof args === 'string') return args;
  if (typeof args !== 'object' || args === null || Array.isArray(args)) return null;
  const v = (args as Readonly<Record<string, unknown>>)[key];
  return typeof v === 'string' ? v : null;
}

// ---------------------------------------------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------------------------------------------

export const LIBRARY_COMMANDS: readonly CommandDef[] = [
  chartCommand(
    {
      id: 'project.open',
      title: m.cmdOpen,
      category: 'File',
      icon: 'FolderOpen',
      level: 'guided',
      aliases: ['open chart', 'open project', 'edit chart', '打开谱面'],
    },
    (ctx, p) => openProject(ctx, p),
  ),
  chartCommand(
    {
      id: 'project.newDifficulty',
      title: m.cmdNewDifficulty,
      category: 'File',
      icon: 'CopyPlus',
      level: 'guided',
      aliases: ['difficulty', 'new level', 'another difficulty', 'EZ', 'HD', 'IN', 'AT', '新难度', '难度'],
    },
    async (ctx, p) => {
      const lib = libraryStore(ctx);
      // The dialog lives on the Library page: go there first when a chart is open.
      if (lib.get().mounted === 0) {
        if (ctx.store !== null) await ctx.nav.closeProject();
        else ctx.nav.goLibrary();
      }
      lib.update({ newDifficulty: p.id, focused: p.id });
    },
  ),
  chartCommand(
    {
      id: 'project.duplicate',
      title: m.cmdDuplicate,
      category: 'File',
      icon: 'Copy',
      level: 'guided',
      aliases: ['copy chart', 'clone chart', 'duplicate project', '复制谱面'],
    },
    (ctx, p) => duplicateProject(ctx, p),
  ),
  chartCommand(
    {
      id: 'project.export',
      title: m.cmdExport,
      category: 'Publish',
      icon: 'PackageOpen',
      level: 'guided',
      aliases: ['export pez', 'package', '导出'],
    },
    (ctx, p) => exportProject(ctx, p),
  ),
  chartCommand(
    {
      id: 'project.rename',
      title: m.cmdRename,
      category: 'File',
      icon: 'Pencil',
      level: 'guided',
      aliases: ['rename chart', 'title', 'chart name', '重命名'],
      args: { kind: 'text', label: { key: m.renameArg } },
    },
    async (ctx, p, args) => {
      const name = textArg(args, 'name');
      if (name !== null) {
        await renameProject(ctx, p, name);
        return;
      }
      // Without a title: edit it in place on the Library page.
      const lib = libraryStore(ctx);
      if (lib.get().mounted === 0) {
        if (ctx.store !== null) await ctx.nav.closeProject();
        else ctx.nav.goLibrary();
      }
      lib.update({ renaming: p.id, focused: p.id });
    },
  ),
  chartCommand(
    {
      id: 'project.trash',
      title: m.cmdTrash,
      category: 'File',
      icon: 'Trash2',
      level: 'guided',
      aliases: ['delete chart', 'remove chart', 'trash', '删除谱面', '回收站'],
    },
    (ctx, p) => trashProject(ctx, p),
  ),
  chartCommand(
    {
      id: 'project.restore',
      title: m.cmdRestore,
      category: 'File',
      icon: 'ArchiveRestore',
      level: 'guided',
      aliases: ['undelete', 'restore chart', 'trash', '恢复谱面'],
    },
    async (ctx, p) => {
      if (p.trashedAt === null) {
        ctx.ui.toast({ key: m.notInTrash, params: { name: displayName(ctx, p) } }, { kind: 'info' });
        return;
      }
      await restoreProject(ctx, p);
    },
  ),
  chartCommand(
    {
      id: 'project.deleteForever',
      title: m.cmdDeleteForever,
      category: 'File',
      icon: 'Trash',
      level: 'guided',
      aliases: ['delete forever', 'purge', '永久删除'],
    },
    async (ctx, p) => {
      if (p.trashedAt === null) {
        ctx.ui.toast({ key: m.deleteNeedsTrash }, { kind: 'info' });
        return;
      }
      await deleteForever(ctx, [p]);
    },
  ),
  chartCommand(
    {
      id: 'project.recoveryData',
      title: m.cmdRecovery,
      category: 'Help',
      icon: 'LifeBuoy',
      level: 'full',
      aliases: ['recovery', 'journal', 'bug report', 'crash'],
    },
    (ctx, p) => downloadRecoveryData(ctx, p),
  ),
  {
    id: 'project.emptyTrash',
    title: m.cmdEmptyTrash,
    category: 'File',
    icon: 'Trash',
    level: 'guided',
    aliases: ['empty trash', 'clear trash', '清空回收站'],
    async run(ctx) {
      const list = trashedProjects(await refreshProjects(ctx));
      if (list.length === 0) {
        ctx.ui.toast({ key: m.trashEmptyTitle }, { kind: 'info' });
        return;
      }
      await deleteForever(ctx, list);
    },
  },
  {
    id: 'project.showTrash',
    title: m.cmdShowTrash,
    category: 'File',
    icon: 'Trash2',
    level: 'guided',
    aliases: ['trash', 'deleted charts', 'recycle bin', '回收站'],
    async run(ctx) {
      if (ctx.store !== null) await ctx.nav.closeProject();
      window.location.hash = '#/trash';
    },
  },
  {
    id: 'project.backupAll',
    title: m.cmdBackupAll,
    category: 'File',
    icon: 'HardDriveDownload',
    level: 'guided',
    aliases: ['backup', 'download all', 'export all', 'save all', '备份'],
    async run(ctx) {
      await backupAll(ctx);
    },
  },
  {
    id: 'project.protect',
    title: m.cmdProtect,
    category: 'File',
    icon: 'ShieldCheck',
    level: 'guided',
    aliases: ['persistent storage', 'protect', 'keep data', 'storage'],
    async run(ctx) {
      await protectCharts(ctx);
    },
  },
];

// ---------------------------------------------------------------------------------------------------------------
// Help
// ---------------------------------------------------------------------------------------------------------------

const HELP_TOPICS: readonly HelpTopicDef[] = [
  { id: 'library', title: m.helpLibraryTitle, body: m.helpLibraryBody, level: 'guided' },
  { id: 'backup', title: m.helpBackupTitle, body: m.helpBackupBody, level: 'guided' },
];

// ---------------------------------------------------------------------------------------------------------------
// "Switch to it" requests from other tabs
// ---------------------------------------------------------------------------------------------------------------

function answerFocusRequests(s: AppServices): () => void {
  let stopBlink: (() => void) | null = null;
  const off = listenFocusRequests((id) => {
    const store = s.store;
    if (store?.projectId !== id) return;
    try {
      window.focus();
    } catch {
      // Some browsers refuse; the blinking title still points the user here.
    }
    stopBlink?.();
    const name = store.doc.meta.name.trim() === '' ? s.i18n.t(m.untitled) : store.doc.meta.name;
    stopBlink = blinkTitle(s.i18n.t(m.titleBlink, { name }));
    s.ui.toast({ key: m.focusRequested, params: { name } }, { kind: 'info', durationMs: 8000 });
  });
  return () => {
    off();
    stopBlink?.();
  };
}

export default {
  id: 'library',
  routes: [
    { path: '#/', component: LibraryRoute },
    { path: '#/trash', component: TrashRoute },
  ],
  commands: LIBRARY_COMMANDS,
  helpTopics: HELP_TOPICS,
  init(s) {
    return answerFocusRequests(s);
  },
} satisfies FeatureModule;
